import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Briefcase, Search, FileText, Calendar,
  Building2, FileCheck, Users, Receipt, Video, Settings,
  BarChart3, ChevronDown, Plus, Moon, Sun, LogOut,
  Bell, HelpCircle, Sparkles, Shield, Lock, User,
  ScrollText, ShieldAlert, Globe, Activity, Clock, ShieldCheck,
  Home, Info, Package, BookOpen, LifeBuoy, Mail, Trash2
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import VSKRLogo from '../VSKRLogo';

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/app' },
  { id: 'matters', label: 'Matters / Cases', icon: Briefcase, path: '/app/matters' },
  { id: 'research', label: 'AI Research', icon: Search, path: '/app/research', badge: 'AI' },
  { id: 'documents', label: 'Documents', icon: FileText, path: '/app/documents' },
  { id: 'courts', label: 'Court Tracking', icon: Building2, path: '/app/courts' },
  { id: 'contracts', label: 'Contracts (CLM)', icon: FileCheck, path: '/app/contracts' },
  { id: 'clients', label: 'Clients', icon: Users, path: '/app/clients' },
  { id: 'billing', label: 'Billing', icon: Receipt, path: '/app/billing' },
  { id: 'avatar', label: 'AI Avatar', icon: Video, path: '/app/avatar', badge: 'NEW' },
  { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/app/calendar' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/app/analytics' },
];

// Public information links (accessible to everyone — NOT subject to page permissions)
const publicInfoGroups = [
  {
    heading: 'VSKR Legal',
    items: [
      { id: 'pub_home', label: 'Home', icon: Home, path: '/' },
      { id: 'pub_about', label: 'About', icon: Info, path: '/about' },
      { id: 'pub_products', label: 'Products', icon: Package, path: '/products' },
      { id: 'pub_lios', label: 'LIOS', icon: BookOpen, path: '/products/lios' },
    ],
  },
  {
    heading: 'Help & Legal',
    items: [
      { id: 'pub_help', label: 'Help', icon: LifeBuoy, path: '/help' },
      { id: 'pub_contact', label: 'Contact', icon: Mail, path: '/contact' },
      { id: 'pub_privacy', label: 'Privacy', icon: FileText, path: '/privacy' },
      { id: 'pub_terms', label: 'Terms', icon: ScrollText, path: '/terms' },
      { id: 'pub_delete', label: 'Delete Data', icon: Trash2, path: '/account-data-deletion' },
    ],
  },
];

const adminMenuItems = [
  { id: 'admin_dashboard', label: 'Admin Dashboard', icon: Shield, path: '/admin' },
  { id: 'admin_users', label: 'User Management', icon: Users, path: '/admin/users' },
  { id: 'admin_access', label: 'Page Access', icon: Lock, path: '/admin/access' },
  { id: 'admin_security_dashboard', label: 'Security Dashboard', icon: ShieldCheck, path: '/admin/security' },
  { id: 'admin_activity', label: 'Activity Log', icon: ScrollText, path: '/admin/activity' },
  { id: 'admin_security_events', label: 'Security Events', icon: ShieldAlert, path: '/admin/security-events' },
  { id: 'admin_visitors', label: 'Visitor Activity', icon: Globe, path: '/admin/visitors' },
  { id: 'admin_user_activity', label: 'User Activity', icon: Activity, path: '/admin/user-activity' },
  { id: 'admin_sessions', label: 'Session Timeline', icon: Clock, path: '/admin/sessions' },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { currentWorkspace, workspaces, switchWorkspace } = useWorkspace();
  const { user, isAdmin, logout, hasPageAccess } = useAuth();
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter menu items based on page permissions
  const visibleMenuItems = menuItems.filter(item => hasPageAccess(item.id));

  return (
    <motion.aside
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      className="fixed left-0 top-0 h-screen w-[280px] z-50 flex flex-col"
      style={{
        background: isDark
          ? 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.95) 100%)'
          : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(250,251,252,0.95) 100%)',
        backdropFilter: 'blur(20px)',
        borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
      }}
    >
      {/* Logo */}
      <div className="p-4 border-b border-opacity-20" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
        <motion.div
          className="flex flex-col items-center justify-center cursor-pointer"
          whileHover={{ scale: 1.05 }}
          onClick={() => navigate('/app')}
        >
          <VSKRLogo size={70} animated={false} isDark={isDark} showText={true} />
        </motion.div>
      </div>

      {/* User Profile Section */}
      {user && (
        <div className="px-4 py-3 border-b border-opacity-20" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
          <motion.button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full p-3 rounded-xl glass-card flex items-center justify-between"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium truncate max-w-[140px]">{user.full_name}</p>
                <p className="text-xs text-gray-500 capitalize flex items-center gap-1">
                  {isAdmin && <Shield className="w-3 h-3 text-purple-500" />}
                  {user.role}
                </p>
              </div>
            </div>
            <motion.div animate={{ rotate: userMenuOpen ? 180 : 0 }}>
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {userMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 overflow-hidden"
              >
                <motion.button
                  onClick={() => { navigate('/app/settings'); setUserMenuOpen(false); }}
                  className="w-full p-3 rounded-lg flex items-center gap-3 hover:bg-gray-500/10"
                  whileHover={{ x: 4 }}
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm">Profile Settings</span>
                </motion.button>
                <motion.button
                  onClick={handleLogout}
                  className="w-full p-3 rounded-lg flex items-center gap-3 text-red-500 hover:bg-red-500/10"
                  whileHover={{ x: 4 }}
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Logout</span>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Workspace Selector */}
      <div className="p-4">
        <motion.button
          onClick={() => setWorkspaceOpen(!workspaceOpen)}
          className="w-full p-3 rounded-xl glass-card flex items-center justify-between"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center gap-3">
            <span className="text-xl">{currentWorkspace.icon}</span>
            <div className="text-left">
              <p className="text-sm font-medium truncate max-w-[140px]">{currentWorkspace.name}</p>
              <p className="text-xs text-gray-500 capitalize">{currentWorkspace.type}</p>
            </div>
          </div>
          <motion.div animate={{ rotate: workspaceOpen ? 180 : 0 }}>
            <ChevronDown className="w-4 h-4" />
          </motion.div>
        </motion.button>

        <AnimatePresence>
          {workspaceOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2 overflow-hidden"
            >
              {workspaces.map((ws) => (
                <motion.button
                  key={ws.id}
                  onClick={() => { switchWorkspace(ws); setWorkspaceOpen(false); }}
                  className={`w-full p-3 rounded-lg flex items-center gap-3 mb-1 transition-all ${
                    currentWorkspace.id === ws.id
                      ? 'bg-blue-500/10 text-blue-500'
                      : 'hover:bg-gray-500/10'
                  }`}
                  whileHover={{ x: 4 }}
                >
                  <span>{ws.icon}</span>
                  <span className="text-sm">{ws.name}</span>
                </motion.button>
              ))}
              <motion.button
                className="w-full p-3 rounded-lg flex items-center gap-3 text-blue-500 hover:bg-blue-500/10"
                whileHover={{ x: 4 }}
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm">Create Workspace</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 overflow-y-auto">
        {visibleMenuItems.map((item, index) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/' && location.pathname.startsWith(item.path));
          const Icon = item.icon;

          return (
            <motion.button
              key={item.id}
              onClick={() => navigate(item.path)}
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`w-full p-3 rounded-xl flex items-center justify-between mb-1 transition-all relative overflow-hidden ${
                isActive
                  ? 'nav-item-active'
                  : 'hover:bg-gray-500/10'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Hover Glow Effect */}
              {hoveredItem === item.id && !isActive && (
                <motion.div
                  layoutId="hoverGlow"
                  className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              )}
              
              <div className="flex items-center gap-3 relative z-10">
                <Icon className={`w-5 h-5 ${isActive ? 'text-white' : ''}`} />
                <span className={`text-sm font-medium ${isActive ? 'text-white' : ''}`}>
                  {item.label}
                </span>
              </div>
              
              {item.badge && (
                <span className={`px-2 py-0.5 text-xs rounded-full relative z-10 ${
                  item.badge === 'AI' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                }`}>
                  {item.badge}
                </span>
              )}
            </motion.button>
          );
        })}

        {/* Admin Section - Only visible to admins */}
        {isAdmin && (
          <div className="mt-4 pt-4 border-t border-gray-500/20">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3">Admin</p>
            {adminMenuItems.map((item, index) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/admin' && location.pathname.startsWith(item.path));
              const Icon = item.icon;

              return (
                <motion.button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (visibleMenuItems.length + index) * 0.05 }}
                  className={`w-full p-3 rounded-xl flex items-center justify-between mb-1 transition-all relative overflow-hidden ${
                    isActive
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'hover:bg-purple-500/10'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {hoveredItem === item.id && !isActive && (
                    <motion.div
                      layoutId="adminHoverGlow"
                      className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    />
                  )}
                  
                  <div className="flex items-center gap-3 relative z-10">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-purple-500'}`} />
                    <span className={`text-sm font-medium ${isActive ? 'text-white' : ''}`}>
                      {item.label}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}

        {/* Public Information links (accessible to everyone; not permission-gated) */}
        {publicInfoGroups.map((group) => (
          <div key={group.heading} className="mt-4 pt-4 border-t border-gray-500/20">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3">{group.heading}</p>
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <motion.button
                  key={item.id}
                  data-testid={`sidebar-${item.id}`}
                  onClick={() => navigate(item.path)}
                  className={`w-full p-2.5 rounded-xl flex items-center gap-3 mb-1 transition-all ${active ? 'bg-blue-500/10 text-blue-500' : 'hover:bg-gray-500/10'}`}
                  whileHover={{ x: 4 }}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </motion.button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="p-4 border-t border-opacity-20" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
        {/* AI Copilot Card */}
        <motion.div
          className="p-4 rounded-xl mb-4 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(30,58,95,0.9) 0%, rgba(45,140,255,0.9) 100%)'
          }}
          whileHover={{ scale: 1.02 }}
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <Sparkles className="w-6 h-6 text-yellow-400 mb-2" />
          <p className="text-white font-medium text-sm">AI Copilot</p>
          <p className="text-white/70 text-xs mt-1">Ask anything about law</p>
        </motion.div>

        {/* Quick Actions */}
        <div className="flex items-center justify-between">
          <motion.button
            onClick={toggleTheme}
            className="p-3 rounded-xl glass-card"
            whileHover={{ scale: 1.1, rotate: 15 }}
            whileTap={{ scale: 0.9 }}
          >
            {isDark ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-blue-500" />}
          </motion.button>
          
          <motion.button
            onClick={() => navigate('/app/settings')}
            className="p-3 rounded-xl glass-card"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Settings className="w-5 h-5" />
          </motion.button>
          
          <motion.button
            className="p-3 rounded-xl glass-card relative"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Bell className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">3</span>
          </motion.button>
          
          <motion.button
            onClick={handleLogout}
            className="p-3 rounded-xl glass-card text-red-500 hover:bg-red-500/10"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
